/**
 * What a signed-in user is allowed to do, as one list.
 *
 * The portal used to answer that question in whichever file happened to be
 * asking: the shell compared nav titles against `'Ratings'`, the Singapore
 * module carried its own region gate and its own three role names, and the
 * valuation workflow table named those roles again. Three answers to one
 * question, and adding a fourth product surface meant editing all three.
 *
 * Everything now resolves through this file. A screen asks for a capability,
 * never for a role - `useCan('assignments.export')`, not "is this user an
 * Analyst" - so who holds a capability can change without touching a single
 * call site. Roles map to capabilities in exactly one place, below.
 *
 * None of this is a security boundary. It decides what to draw and what to
 * enable; the API repeats every check and is the only thing standing between a
 * determined caller and the data.
 */

/* ------------------------------------------------------------- the grants -- */

/**
 * A portal role.
 *
 * These are the names the app reasons in. What arrives in the token may be one
 * of these, an AAD group that resolves to one, or a legacy alias - all of that
 * is settled by `normalizeRoles` before anything here is consulted.
 */
export const PORTAL_ROLES = [
  'Admin',
  'Coordinator',
  'Valuer',
  'Analyst',
  'Ratings',
  'User',
  'Reader'
] as const;

export type PortalRole = (typeof PORTAL_ROLES)[number];

/**
 * Names that mean a portal role but do not read like one.
 *
 * Two sources feed this. The portal's own historic role names, which predate
 * this file and are still what `groupRoleMap` produces from AAD groups; and
 * the `IVS.*` app roles, which were granted per user for the Singapore pilot
 * and are still assigned in Entra. Both are aliases rather than roles of their
 * own - a user holding `IVS.Valuer` is a Valuer, and always was.
 */
const ROLE_ALIASES: Record<string, PortalRole> = {
  // The Singapore pilot's app roles.
  'IVS.Valuer': 'Valuer',
  'IVS.Analyst': 'Analyst',
  'IVS.Admin': 'Admin',
  // The portal's existing group-derived roles.
  GlobalAdmin: 'Admin',
  PortfolioCoordinator: 'Coordinator',
  Appraiser: 'Valuer',
  GlobalReader: 'Reader'
};

/**
 * Roles each role carries implicitly.
 *
 * Kept shallow on purpose: one level of expansion, no transitive closure to
 * reason about. An Admin is everything, a Coordinator is a senior Analyst, and
 * a Valuer and an Analyst are peers over different parts of the same record.
 */
const ROLE_IMPLIES: Record<PortalRole, PortalRole[]> = {
  Admin: ['Coordinator', 'Valuer', 'Analyst', 'User', 'Reader'],
  Coordinator: ['Valuer', 'Analyst', 'User', 'Reader'],
  Valuer: ['User', 'Reader'],
  Analyst: ['User', 'Reader'],
  /*
   * Ratings implies nothing, and nothing implies Ratings.
   *
   * It is a separate product sold to different people, and the shell has
   * always treated it that way - a Ratings user saw the Ratings screens
   * instead of the portal, not as well as it. Because it grants no other
   * capability, that behaviour now falls out of the ordinary nav filter
   * rather than needing the title comparison the shell used to carry.
   */
  Ratings: [],
  User: ['Reader'],
  Reader: []
};

/**
 * Token values to portal roles, with the aliases and the implied roles applied.
 *
 * Unknown values are dropped rather than carried through: a role the app has
 * never heard of grants nothing, and letting it travel further would only mean
 * discovering that somewhere less convenient.
 */
export function normalizeRoles(raw: readonly string[] | undefined): Set<PortalRole> {
  const roles = new Set<PortalRole>();

  for (const value of raw ?? []) {
    const direct = PORTAL_ROLES.find((role) => role === value);
    const resolved = direct ?? ROLE_ALIASES[value];

    if (!resolved) {
      continue;
    }

    roles.add(resolved);

    for (const implied of ROLE_IMPLIES[resolved]) {
      roles.add(implied);
    }
  }

  return roles;
}

/* ------------------------------------------------------- the capabilities -- */

/**
 * Everything a user can be permitted to do, namespaced by the surface it
 * belongs to. A new screen adds entries here first; the screen itself only
 * ever names one of these strings.
 */
export const CAPABILITIES = [
  /* assignments - the valuation record and the work around it --------------- */
  'assignments.view',
  'assignments.create',
  'assignments.edit',
  'assignments.export',
  'assignments.delete',
  'assignments.restore',
  /** Complete directly, in a market whose workflow has no review step. */
  'assignments.complete',
  /** Hand finished work to a reviewer. */
  'assignments.submitForReview',
  /** Review someone else's work: complete it, or send it back for rework. */
  'assignments.review',
  'assignments.reject',
  /** Put a record on hold - Singapore's "KIV". */
  'assignments.hold',
  'assignments.reactivate',
  /** Internal notes the requesting client never sees. */
  'assignments.remarks.internal.read',
  'assignments.remarks.internal.write',
  'assignments.remarks.client.write',
  'assignments.masterData',
  /**
   * Assist: the drafted figures, the morning briefing, the request composer
   * and answers across the records. Seeing a draft is not releasing it - that
   * stays with `complete` / `review`.
   */
  'assignments.assist',
  /** Upload and draft a bulk batch. Releasing its rows still needs `complete` / `review`. */
  'assignments.bulk',

  /* crm - clients, contacts, people, and opportunity intake ---------------- */
  'crm.view',
  /** Create and edit companies, contacts and people. */
  'crm.edit',
  'opportunities.view',
  'opportunities.edit',
  /** Approve a submitted opportunity, or return it to its owner. */
  'opportunities.approve',

  /* the other product surfaces --------------------------------------------- */
  'reportStudio.view',
  'reportStudio.authorTemplates',
  'agents.view',
  'agents.build',
  'map.view',
  'vault.view',
  'connectors.view',
  'ratings.view',

  /* the platform itself ---------------------------------------------------- */
  /** Open Settings: scoped feature flags, formats, role defaults and rules. */
  'platform.settings'
] as const;

export type Capability = (typeof CAPABILITIES)[number];

/**
 * Who holds what.
 *
 * Read down a column to answer "what can an Analyst do"; read across a row to
 * answer "who may complete a record". The second question is the one that gets
 * asked in a meeting, which is why the table is written this way round.
 */
const CAPABILITY_ROLES: Record<Capability, PortalRole[]> = {
  'assignments.view': ['Admin', 'Coordinator', 'Valuer', 'Analyst', 'User', 'Reader'],
  'assignments.create': ['Admin', 'Coordinator', 'Valuer', 'Analyst'],
  'assignments.edit': ['Admin', 'Coordinator', 'Valuer', 'Analyst'],
  'assignments.export': ['Admin', 'Coordinator', 'Analyst'],
  'assignments.delete': ['Admin', 'Coordinator', 'Analyst'],
  'assignments.restore': ['Admin'],
  // Where there is no review step, valuers sign off their own work; analysts
  // prepare it but do not complete it.
  'assignments.complete': ['Admin', 'Valuer'],
  // Where there is one, everyone who prepares work submits it, and a valuer or
  // a coordinator reviews it - never their own, which the workflow enforces.
  'assignments.submitForReview': ['Admin', 'Coordinator', 'Valuer', 'Analyst'],
  'assignments.review': ['Admin', 'Coordinator', 'Valuer'],
  'assignments.reject': ['Admin', 'Coordinator', 'Valuer', 'Analyst'],
  'assignments.hold': ['Admin', 'Coordinator', 'Valuer', 'Analyst'],
  'assignments.reactivate': ['Admin'],
  'assignments.remarks.internal.read': ['Admin', 'Coordinator', 'Valuer', 'Analyst'],
  'assignments.remarks.internal.write': ['Admin', 'Valuer'],
  'assignments.remarks.client.write': ['Admin', 'Coordinator', 'Valuer', 'Analyst'],
  'assignments.masterData': ['Admin'],
  // Requestors use Assist too - to raise a request in a sentence and follow it.
  'assignments.assist': ['Admin', 'Coordinator', 'Valuer', 'Analyst', 'User'],
  'assignments.bulk': ['Admin', 'Coordinator', 'Valuer'],

  'crm.view': ['Admin', 'Coordinator', 'Valuer', 'Analyst', 'User', 'Reader'],
  'crm.edit': ['Admin', 'Coordinator', 'Valuer', 'Analyst'],
  'opportunities.view': ['Admin', 'Coordinator', 'Valuer', 'Analyst', 'User', 'Reader'],
  'opportunities.edit': ['Admin', 'Coordinator', 'Valuer', 'Analyst'],
  'opportunities.approve': ['Admin', 'Coordinator'],

  'reportStudio.view': ['Admin', 'Coordinator', 'Valuer', 'Analyst'],
  'reportStudio.authorTemplates': ['Admin', 'Coordinator'],
  'agents.view': ['Admin', 'Coordinator', 'Valuer', 'Analyst', 'User'],
  'agents.build': ['Admin', 'Coordinator', 'Valuer', 'Analyst'],
  'map.view': ['Admin', 'Coordinator', 'Valuer', 'Analyst', 'User', 'Reader'],
  'vault.view': ['Admin', 'Coordinator', 'Valuer', 'Analyst', 'User', 'Reader'],
  'connectors.view': ['Admin', 'Coordinator', 'Valuer', 'Analyst'],
  'ratings.view': ['Ratings'],

  'platform.settings': ['Admin']
};

export function capabilitiesForRoles(roles: Set<PortalRole>): Set<Capability> {
  const granted = new Set<Capability>();

  for (const capability of CAPABILITIES) {
    if (CAPABILITY_ROLES[capability].some((role) => roles.has(role))) {
      granted.add(capability);
    }
  }

  return granted;
}

/** The roles that hold a capability - for an "ask your administrator" message. */
export function rolesHolding(capability: Capability): PortalRole[] {
  return CAPABILITY_ROLES[capability] ?? [];
}
